/*
 * Copyright (c) 2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */


import React, {useEffect, useState} from "react";
import {useUserInfo} from "authClient";
import UserAvatar from "../UserComponents/UserAvatar.jsx";
import CopyToClipboardComponent from "../CopyToClipboardComponent.jsx";
import Package from "../../../package.json";
import Tooltip from "../Tooltip.jsx";
import {schemaVersions} from "../../Constants.js";

const YourAccountSettings = () => {
    const [backendVersion, setBackendVersion] = useState("");
    const [backendSchemaVersions, setBackendSchemaVersions] = useState("");

    const userAttributes = useUserInfo()
    const fetchData = async () => {
        setBackendSchemaVersions(schemaVersions?.toString() ?? "");
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <div style={{minHeight: "1500px"}}>
                <div className="h-full flex">
                    {/* Static sidebar for desktop */}
                    <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <div className="flex-1 relative z-0 flex overflow-hidden">
                            <main className="flex-1 relative z-0 overflow-y-auto focus:outline-hidden xl:order-last">
                                <article>
                                    {/* Profile header */}
                                    <div>
                                        <div>
                                            <img
                                                className="h-32 w-full object-cover lg:h-48 rounded-lg"
                                                src="https://images.unsplash.com/photo-1639718561716-b59d3995d886?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=3570&q=80"
                                                alt=""
                                            />
                                        </div>
                                        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                                            <div className="-mt-12 sm:-mt-16 sm:flex sm:items-end sm:space-x-5">
                                                <div className="flex">
                                                    <div className="rounded-full ring-4 ring-white h-32 w-32">
                                                        <UserAvatar
                                                            firstName={userAttributes.firstName}
                                                            allowEdit={true}
                                                            surname={userAttributes.surname}
                                                            userId={userAttributes.indexKey}
                                                            size={"full"}
                                                            displayStatus={false}
                                                            status={"online"}
                                                        />
                                                    </div>
                                                </div>
                                                <div
                                                    className="mt-6 sm:flex-1 sm:min-w-0 sm:flex sm:items-center sm:justify-end sm:space-x-6 sm:pb-1">
                                                    <div className="sm:hidden 2xl:block mt-6 min-w-0 flex-1">
                                                        <h1 className="text-2xl font-bold text-gray-900 truncate">
                                                            {(userAttributes.firstName || "") + " " + (userAttributes.surname ?? "")}
                                                        </h1>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>


                                    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                                        <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Vorname</dt>
                                                <dd className="mt-1 text-sm text-gray-900">{userAttributes.firstName}</dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Nachname</dt>
                                                <dd className="mt-1 text-sm text-gray-900">{userAttributes.surname}</dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Email</dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent text={userAttributes.email}/>
                                                </dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Tenant
                                                    Role
                                                </dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent text={userAttributes.role}/>
                                                </dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Software
                                                    Version (Frontend/ Backend)
                                                </dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent
                                                        text={`${Package.version} / ${backendVersion}`}/>
                                                </dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Environment
                                                    name
                                                </dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent text={import.meta.env.MODE}/>
                                                </dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Tenant
                                                    Name
                                                </dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent
                                                        text={
                                                            <Tooltip delay={2000} text={
                                                                <div>TenantId: {userAttributes.tenantId}</div>
                                                            }>
                                                                {userAttributes.tenantName ?? (userAttributes.tenantId)}
                                                            </Tooltip>
                                                        }
                                                    />
                                                </dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Tenant
                                                    Id
                                                </dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent
                                                        text={userAttributes.tenantId}/>
                                                </dd>
                                            </div>
                                            <div className="sm:col-span-1">
                                                <dt className="text-sm font-medium text-gray-500">Schema
                                                    Version (Frontend/ Backend)
                                                </dt>
                                                <dd className="mt-1 text-sm text-gray-900">
                                                    <CopyToClipboardComponent
                                                        text={`${schemaVersions} / ${backendSchemaVersions}`}/>
                                                </dd>
                                            </div>
                                        </dl>
                                    </div>


                                </article>
                            </main>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default YourAccountSettings;